import { useTranslation } from "react-i18next";
import { islandContainerClasses } from "@/constants/layout";
import favicon from "/favicon.svg";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Menu,
  Music,
  X,
  Sparkles,
  MessageSquare,
  BookOpenCheck,
  Award,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { StreakTracker } from "@/components/streaks/StreakTracker";
import type { UserProfile } from "@/types/user";

const NavLink = ({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) => {
  const location = useLocation();
  
  // Smart active state logic to handle subsection routes
  const isActive = (() => {
    const currentPath = location.pathname;
    
    // Exact match
    if (currentPath === to) return true;
    
    // Handle scenarios section (home page and all scenario-related routes)
    if (to === "/") {
      return currentPath === "/" || 
             currentPath.startsWith("/scenario") ||
             currentPath.startsWith("/scenarios");
    }
    
    // Handle islands section
    if (to === "/islands") {
      return currentPath.startsWith("/islands");
    }
    
    // Handle review section
    if (to === "/review") {
      return currentPath.startsWith("/review");
    }
    
    // Default: check if current path starts with the target path
    return currentPath.startsWith(to + "/");
  })();

  return (
    <Link
      to={to}
      className={`flex items-center gap-2 px-3 pt-4 pb-2 font-medium transition-colors relative ${
        isActive
          ? "text-brand-button"
          : "text-muted-foreground hover:text-brand-button-hover"
      }`}
    >
      {children}
      <div
        className={`absolute bottom-0 left-0 right-0 h-[3px] transition-colors ${
          isActive ? "bg-brand-button" : "bg-transparent"
        }`}
      />
    </Link>
  );
};

import { getActiveDays } from "@/utils/streakUtils";

interface HeaderProps {
  onLogout: () => void;
  userProfile: UserProfile | null;
}

export function Header({ onLogout, userProfile }: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  
  // Monitor body class to detect when audio is playing on full dialogue pages
  // This prevents sticky header from creating stacking context conflicts with sticky audio controls
  useEffect(() => {
    const checkAudioPlayingState = () => {
      setIsAudioPlaying(document.body.classList.contains("audio-playing"));
    };
    
    // Check initial state
    checkAudioPlayingState();
    
    // Use MutationObserver to watch for body class changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          checkAudioPlayingState();
        }
      });
    });
    
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);
  
  // Dynamic header classes based on audio playback state
  // When audio is playing on full dialogue pages, remove sticky to avoid stacking conflicts
  const isFullDialoguePage = location.pathname.includes('/scenario-full/');
  const shouldRemoveSticky = isFullDialoguePage && isAudioPlaying;
  const headerClasses = shouldRemoveSticky 
    ? "bg-white/80 backdrop-blur-lg border-b border-white/30 z-50"
    : "bg-white/80 backdrop-blur-lg border-b border-white/30 z-50 sticky top-0";

  const handleNavigate = (path: string) => {
    setMenuOpen(false);
    navigate(path);
  };

  const currentStreak = userProfile?.currentStreak || 0;
  const activeDays = getActiveDays(currentStreak, userProfile?.lastSessionCompletionDate);

  return (
    <header className={headerClasses}>
      <div
        className={`${islandContainerClasses} flex justify-between items-center`}
      >
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center">
            <img
              src={favicon}
              alt="Las Chispas Logo"
              className="h-8 w-auto"
              width="38"
              height="32"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-6 ml-6">
            <NavLink to="/">
              <MessageSquare className="h-4 w-4" />
              {t("scenarios")}
            </NavLink>
            <NavLink to="/islands">
              <Sparkles className="h-4 w-4" />
              <span className="hidden xl:inline">{t("islands")}</span>
              <span className="xl:hidden">{t("islandsCompact")}</span>
            </NavLink>
            <NavLink to="/review">
              <BookOpenCheck className="h-4 w-4" />
              {t("review")}
            </NavLink>
            <NavLink to="/profile/achievements">
              <Award className="h-4 w-4" />
              {t("achievements.achievements")}
            </NavLink>
          </nav>
        </div>

        <div className="flex items-center gap-4">
          {userProfile && (
            <div className="hidden lg:flex">
              <StreakTracker 
                currentStreak={currentStreak} 
                activeDays={activeDays} 
              />
            </div>
          )}
          <a
            href="https://fluencylock.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex"
          >
            <Button
              variant="primary"
              size="sm"
              className="text-white shadow-md transition-colors"
            >
              <Music className="h-4 w-4 mr-2" />
              {t("try")} FluencyLock
            </Button>
            <span className="sr-only">(opens in new tab)</span>
          </a>
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="hover:bg-gray-100"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
            >
              {menuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </Button>

            {menuOpen && (
              <nav className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg">
                <div className="md:hidden border-b border-gray-200 mb-2 pb-2">
                  <button
                    onClick={() => handleNavigate("/")}
                    className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    <MessageSquare className="h-4 w-4" />
                    {t("scenarios")}
                  </button>
                  <button
                    onClick={() => handleNavigate("/islands")}
                    className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    <Sparkles className="h-4 w-4" />
                    {t("islands")}
                  </button>
                  <button
                    onClick={() => handleNavigate("/review")}
                    className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    <BookOpenCheck className="h-4 w-4" />
                    {t("review")}
                  </button>
                  <button
                    onClick={() => handleNavigate("/profile/achievements")}
                    className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    <Award className="h-4 w-4" />
                    {t("achievements.achievements")}
                  </button>
                </div>

                <a
                  href="https://fluencylock.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex sm:hidden items-center gap-2 w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                >
                  <Music className="h-4 w-4" />
                  {t("try")} FluencyLock
                </a>
                <button
                  onClick={() => handleNavigate("/profile")}
                  className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                >
                  {t("profile")}
                </button>
                <button
                  onClick={onLogout}
                  className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                >
                  {t("logout")}
                </button>
              </nav>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
